import React, { Suspense, useState, useEffect } from 'react';

import classnames from 'classnames';

import { CashBack } from '@lt/components';
import { useTranslation } from '@lt/localization';
import HotelRating from 'src/components/HotelRating';
import { useForceRender } from 'src/hooks/useForceRender';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import EmptyMap from 'src/components/YandexMap/components/EmptyMap';

import type { StepName } from '@lt/api/dist/methods/stats/types';
import HintFactory from '../../../../components/Hint/HintFactory';

import Container from '../../components/Container';
import type { RelinkHotelsType } from '../../components/HotelRelink';
import HotelRelink from '../../components/HotelRelink';
import MobilePage from '../../components/MobilePage';
import HeaderContainer from '../Header';
import { COMPONENT_ID, HOTEL_MOBILE_PAGES } from '../../constants';
import FacilitiesContainer from '../FacilitiesContainer';
import SearcherFixedButtonContainer from '../SearcherFixedButton';
import HotelMap from '../HotelMap';
import HotelInfo from '../HotelInfo';
import Gallery from '../Gallery';
import RatingBadge from '../RatingBadge';
import ReviewsDesktop from '../../components/Reviews/OldReviewsComponents/ReviewsDesktop';
import ReviewsMobile from '../../components/Reviews/ReviewsMobile';
import ExtraItemsContainer from '../ExtraItemsContainer';
import FortunaJumbotronContainer from '../FortunaJumbotronContainer';
import ExperienceContainer from '../ExperienceContainer';
import Paper from '../../components/Paper';
import HotelReviewsMobile from '../HotelReviewsMobile';
import AboutHotelMobile from '../AboutHotelMobile';

import IncludedServicesHint from './IncludedServicesHint';

import styles from './layout.module.css';

export interface LayoutMobileProps {
  page: string;
  showPage: (value: string | null) => void;
  isRoulette: boolean;
  linkedHotels: RelinkHotelsType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchParams: any;
  isWhitelabel: boolean;
  cashbackCount: number;
  cashbackType: string;
  isHotelOnly: boolean;
  isMobile: boolean;
  sendPageview: (page: StepName[keyof StepName]) => void;
  setVariantReviews: (variant: 'new' | 'old') => void;
  sendTargetadsPageview: () => void;
}

const LayoutMobile = (props: LayoutMobileProps) => {
  const {
    page,
    showPage,
    isRoulette,
    linkedHotels,
    searchParams,
    isWhitelabel,
    cashbackCount,
    cashbackType,
    isHotelOnly,
    isMobile,
    sendPageview,
    setVariantReviews,
    sendTargetadsPageview,
  } = props;
  const { t } = useTranslation();

  useEffect(() => {
    if (isWhitelabel) {
      setVariantReviews('old');
    } else {
      setVariantReviews('new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    switch (page) {
      case 'searcher':
        sendPageview(
          isHotelOnly
            ? 'hotel_room_offers_hotels_only'
            : 'hotel_room_offers_tours',
        );

        break;
      case 'reviews':
        sendPageview(
          isHotelOnly ? 'hotel_reviews_hotels_only' : 'hotel_reviews_tours',
        );
        sendTargetadsPageview();
        break;
      default:
    }
  }, [page, sendPageview, isHotelOnly, sendTargetadsPageview]);
  const isSearcher = page === HOTEL_MOBILE_PAGES.SEARCHER;
  const isMap = page === HOTEL_MOBILE_PAGES.MAP;
  const isFixedButtonVisible = !(isSearcher || isMap);

  const { countForceRender } = useForceRender();

  const [isSSR, setIsSSR] = useState(true);

  useEffect(() => {
    setIsSSR(false);
  }, []);

  return (
    <div
      className={classnames('hotel-mobile', styles.container, {
        [styles.containerPaddingBottom]: isFixedButtonVisible,
      })}
    >
      <HintFactory autoPosition events attribute="data-text-hint" isMobile />
      <IncludedServicesHint isMobile />
      {page ? (
        <MobilePage
          goBack={() => showPage(null)}
          page={page}
          isFullscreen={isMap}
        />
      ) : (
        <div>
          <Container isMobile white marginBottom={0}>
            <HeaderContainer isHotelOnly={isHotelOnly} />
          </Container>
          <Container isMobile fullWidth>
            {isRoulette ? (
              <FortunaJumbotronContainer isMobile />
            ) : (
              <>
                {!isWhitelabel && cashbackCount > 0 && (
                  <CashBack
                    className={styles.cashBack}
                    count={`+${cashbackCount}`}
                    size="s"
                    isMobile
                    type={cashbackType}
                    cashbackText
                  />
                )}
                <RatingBadge isMobile />
                <Gallery />
              </>
            )}
          </Container>

          <Container isMobile fullWidth id={COMPONENT_ID.EXPERIENCE}>
            <ExperienceContainer isMobile fullWidth />
          </Container>

          {isRoulette ? (
            <Container isMobile>
              <Paper padding={0}>
                <ExtraItemsContainer />
              </Paper>
            </Container>
          ) : (
            <HotelInfo />
          )}

          <>
            <HotelRating key={countForceRender} />
          </>

          {!isRoulette && <HotelReviewsMobile />}

          {!isWhitelabel ? (
            <Container isMobile white>
              <ReviewsMobile />
            </Container>
          ) : null}

          {!isRoulette && <FacilitiesContainer isMobile />}

          <Container isMobile>
            {isSSR ? null : (
              <ErrorBoundary
                fallback={<EmptyMap isMobile />}
                showDialog={false}
              >
                <Suspense fallback={null}>
                  <HotelMap
                    className={styles.hotelMapContainer}
                    onExpand={() => showPage(HOTEL_MOBILE_PAGES.MAP)}
                    height="200px"
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </Container>

          {!isRoulette && <AboutHotelMobile />}

          {!isWhitelabel && linkedHotels.length > 0 && (
            <Container
              key={countForceRender}
              className={styles.relinkContainer}
              isMobile
              white
            >
              <div className={styles.hotelRelinkHeading}>
                {t('hotel.similarHotels')}
              </div>
              <HotelRelink hotels={linkedHotels} searchParams={searchParams} />
            </Container>
          )}
        </div>
      )}

      {isFixedButtonVisible && (
        <SearcherFixedButtonContainer isHotelOnly={isHotelOnly} />
      )}

      {isSSR && !isMobile && (
        <div style={{ display: 'none' }}>
          <ReviewsDesktop />
        </div>
      )}
    </div>
  );
};

export default LayoutMobile;
